import { createLimsClient, LimsApiClient } from '@mariana/labos-client';
import type { EntityType, Attribute } from '@mariana/labos-client';

const runtime = typeof window !== 'undefined' ? window.__LIMS_CONFIG__ : undefined;
const API_BASE_URL =
  runtime?.baseUrl ??
  process.env.REACT_APP_LABOS_SERVER_URL ??
  process.env.LABOS_SERVER_URL ??
  '';

const _client = createLimsClient({
  baseUrl: API_BASE_URL,
});

const attrCache = new Map<EntityType, { data: Attribute[]; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;
const inflightRequests = new Map<EntityType, Promise<Attribute[]>>();

/**
 * Cached wrapper around getAttributes. Attributes rarely change within a
 * session, but multiple components (ColumnPicker, EntityFilters, EntityDetail,
 * resolveSourceColumns) all call this independently.
 */
function getCachedAttributes(entityType: EntityType): Promise<Attribute[]> {
  const cached = attrCache.get(entityType);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  const inflight = inflightRequests.get(entityType);
  if (inflight) return inflight;

  const request = _client.getAttributes(entityType).then(data => {
    attrCache.set(entityType, { data, ts: Date.now() });
    inflightRequests.delete(entityType);
    return data;
  }).catch(err => {
    inflightRequests.delete(entityType);
    throw err;
  });

  inflightRequests.set(entityType, request);
  return request;
}

export const limsClient: LimsApiClient = new Proxy(_client, {
  get(target, prop, receiver) {
    if (prop === 'getAttributes') return getCachedAttributes;
    return Reflect.get(target, prop, receiver);
  },
});

export type { LimsApiClient };

/** The server's message without the client's "LIMS API error: " prefix, for inline display. */
export function apiErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  return message.replace(/^LIMS API error: /, '');
}

/** One entry of a 400's `details`: the entity routes send `{ attribute, error }`; other routes send strings. */
const detailText = (d: unknown): string => {
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>;
    if (typeof o.error === 'string') return o.error;
    if (typeof o.message === 'string') return o.message;
  }
  return JSON.stringify(d);
};

/**
 * The error a failed response carries, with the validator's details when
 * there are any: the entity routes answer a bad attribute with
 * `{ error: 'Attribute validation failed…', details: [{ attribute, error }] }`,
 * and the detail is what the operator needs to see.
 */
async function responseError(res: Response): Promise<Error> {
  let message = `${res.status} ${res.statusText}`;
  try {
    const body = await res.json();
    if (body?.error) {
      const details = Array.isArray(body.details) ? body.details.map(detailText).join('; ') : typeof body.details === 'string' ? body.details : '';
      message = details ? `${body.error}: ${details}` : String(body.error);
    }
  } catch { /* not JSON */ }
  return new Error(message);
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, init);
  if (!res.ok) throw await responseError(res);
  return res.json() as Promise<T>;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function patchAssay(assayId: number, attributes: Record<string, unknown>): Promise<void> {
  await apiFetch('/api/v0/lims/assays', jsonInit('PATCH', { entities: [{ id: assayId, attributes }] }));
}

export async function postAssay(attributes: Record<string, unknown>): Promise<number> {
  const result = await apiFetch<{ data: Array<{ id: number }> }>('/api/v0/lims/assays', jsonInit('POST', { entities: [{ attributes }] }));
  const id = result.data?.[0]?.id;
  if (typeof id !== 'number') throw new Error('The API did not return the new assay id');
  return id;
}

export interface WorksheetKeyAuditEntry {
  key: string;
  tests: number;
  firstSeen: string;
  lastSeen: string;
  units: string[];
  exampleValue: string | null;
  /** Declared by the assay's analytes definition or matched by its dynamic pattern. */
  declared: boolean;
}

export interface AssayWorksheetKeyAudit {
  assayId: number;
  title: string | null;
  testsWithWorksheet: number;
  hasDefinition: boolean;
  generatedAt: string;
  keys: WorksheetKeyAuditEntry[];
}

export function getAssayWorksheetKeyAudit(assayId: number, refresh = false): Promise<AssayWorksheetKeyAudit> {
  return apiFetch<AssayWorksheetKeyAudit>(`/api/v0/lims/assays/${assayId}/worksheet-key-audit${refresh ? '?refresh=1' : ''}`);
}
